import { useCallback, useEffect, useRef, useState } from 'react'
import { Navigate, Route, Routes } from 'react-router'
import { inject, observer } from 'mobx-react'
import useMediaQuery from '@mui/material/useMediaQuery'

import { withRouterProps } from '../../utils/wrappers'
import { buildInitialContributionDistribution } from '../../utils'
import {
  AdviceTopNav,
  AdviceProgressIndicator,
  AdviceFooter,
  PageLoading,
  LoadingOverlay,
  ErrorPage,
} from '../../components'
import {
  STEPS,
  FIRST_STEP_PATH,
  SSI_STEP_PATH,
  GOALS_STEP_PATH,
  COMPANY_STOCK_STEP_PATH,
  INVESTMENTS_STEP_PATH,
  SUMMARY_STEP_PATH,
} from './steps'
import MonthlyRetirementIncome from './steps/MonthlyRetirementIncome'
import RetirementAge from './steps/RetirementAge'
import SSI from './steps/SSI'
import DontShowAgain from './steps/SSI/DontShowAgain'
import { allSsiExcluded } from './steps/SSI/tableData'
import Contributions from './steps/Contributions'
import Goals from './steps/Goals'
import { hasIncludedGoals } from './steps/Goals/tableData'
import CompanyStock from './steps/CompanyStock'
import { hasCompanyStock } from './steps/CompanyStock/tableData'
import Investments from './steps/Investments'
import { hasInvestmentAccounts } from './steps/Investments/accounts'
import Summary from './steps/Summary'
import ScrollToBottomButton from '../../components/ScrollToBottomButton'
import {
  AdviceLayout,
  StepScroll,
  StepScrollArea,
  StepContent,
  WalkMeThroughLink,
} from './elements'
import {
  AdviceBody,
  AdviceMain,
  AdviceHeyNicPanel,
  AdviceHeyNicOverlay,
  AdviceHeyNicComment,
  PANEL_VIEWPORT,
} from './heyNic'
import { MOBILE_VIEWPORT, TABLET_VIEWPORT } from '../../guided-toolbox/utils/breakpoints'

function Advice({ store, navigate, location }) {
  const [status, setStatus] = useState('loading')
  // A failed accounts fetch skips Investments without failing the entire flow.
  const [accountsFailed, setAccountsFailed] = useState(false)
  const isMobile = useMediaQuery(MOBILE_VIEWPORT)
  // At tablet widths and below, the summary shows “Walk me through” as a button.
  const isMobileOrTablet = useMediaQuery(TABLET_VIEWPORT)
  // Room for the Hey Nic side panel beside the step content; below this the
  // chatbot moves to the in-page comment + overlay (see heyNic/styled.js).
  const hasPanelRoom = useMediaQuery(PANEL_VIEWPORT)
  const [chatOverlayOpen, setChatOverlayOpen] = useState(false)
  // Re-fetching the case after the agent changed something on this step.
  const [caseRefreshing, setCaseRefreshing] = useState(false)
  // Capture the entry origin because redirects and step navigation drop `location.state`.
  const [exitTo] = useState(() => location.state?.from ?? '/home')
  // StepScroll stays mounted between routes, so reset it to keep each step from
  // inheriting the previous step's scroll position.
  const scrollRef = useRef(null)

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0
  }, [location.pathname])

  // Stable callback so ScrollToBottomButton doesn't re-subscribe on every render.
  const getScrollElement = useCallback(() => scrollRef.current, [])

  // Same overlay the make-changes drawer shows on close: the agent changed
  // something behind this step, so re-pull the case and the account breakdown.
  const refreshAdviceCase = useCallback(async () => {
    setCaseRefreshing(true)
    try {
      await Promise.all([store.getBaseCase(), store.getRecommendedCase()])
      await store.refreshAllBreakdown()
    } catch (err) {
      console.error('Failed to refresh the case after an agent change:', err)
    } finally {
      setCaseRefreshing(false)
    }
  }, [store])

  useEffect(() => {
    let active = true

    ;(async () => {
      try {
        // Refetch case and account-derived data on every entry because edits
        // elsewhere do not invalidate the shared store's cached models.
        await Promise.all([
          store.getBaseCase(),
          store.getRecommendedCase(),
          store.getPerson(),
          store.primarySSIBenefits ? null : store.getSSBenefits(),
          store.getUserSettings(),
          // Goals is optional, so a failed fetch skips the step instead of
          // failing the flow.
          store.getGoals().catch(err => console.error('Failed to load goals:', err)),
          // Contributions needs employer accounts loaded by `initializeActiveAccount`.
          store.initializeActiveAccount(),
          store.getAccountInsight(),
        ])

        if (store.includeSpouseInPlanning) {
          await store.getSpouse()
        }

        // Investments is optional; a failed account fetch skips the step.
        try {
          await store.getAccounts()
        } catch (err) {
          console.error('Failed to load accounts:', err)
          if (active) setAccountsFailed(true)
        }

        // Clear stale breakdown data so a failed fetch cannot leave Investments
        // reachable with data from an earlier session.
        store.clearAllBreakdown()

        const breakdownCaseId = (store.modifiedCase || store.recommendedCase)?.caseId
        if (breakdownCaseId != null) {
          await store
            .getAllBreakdown(breakdownCaseId)
            .catch(err => console.error('Failed to load breakdown:', err))
        }

        if (active) setStatus('done')
      } catch (err) {
        console.error(err)
        if (active) setStatus('error')
      }
    })()

    return () => {
      active = false
    }
  }, [store])

  if (status === 'error') {
    return <ErrorPage />
  }

  const engineType = store.modifiedCase ? 'modified' : 'recommended'

  const currentPath = location.pathname.split('/').filter(Boolean).pop()
  const currentStepIndex = STEPS.findIndex(step => step.path === currentPath)

  const goToStep = index => {
    const step = STEPS[index]
    if (step) {
      navigate(`/advice/${step.path}`)
    }
  }

  // Skip SSI if the user opts out and SSI is excluded for everyone in the plan.
  const ssiExcludedAll =
    !!store.primarySSIBenefits &&
    allSsiExcluded({
      spouseIncludedInPlanning: !!store.includeSpouseInPlanning,
      primary: store.primarySSIBenefits,
      spouse: store.spouseSSIBenefits,
    })
  const ssiSkipped = ssiExcludedAll && !!store.userSettings?.hideSsiAdviceStep

  // Show Goals only when the active case includes a current goal.
  const activeCase = store.modifiedCase || store.recommendedCase
  const goalsSkipped =
    !activeCase ||
    !hasIncludedGoals({
      goals: store.currentGoals,
      includedIds: activeCase.includedGoalsIds,
    })

  // Show Company Stock only when the feature is enabled and the case has holdings.
  const companyStockSkipped = !hasCompanyStock({
    features: store.features,
    activeCase,
  })

  // Show Investments only when account metadata and an advised-account
  // breakdown are available.
  const investmentsSkipped =
    accountsFailed ||
    // `sortedAccounts` depends on `person`, which may be null on a cold refresh.
    !store.person ||
    !hasInvestmentAccounts({
      sortedAccounts: store.sortedAccounts,
      allBreakdown: store.allBreakdown,
      personId: store.person?.id,
      spouseId: store.spouse?.id,
    })

  const isSkippable = index => {
    const path = STEPS[index]?.path
    if (path === SSI_STEP_PATH) return ssiSkipped
    if (path === GOALS_STEP_PATH) return goalsSkipped
    if (path === COMPANY_STOCK_STEP_PATH) return companyStockSkipped
    if (path === INVESTMENTS_STEP_PATH) return investmentsSkipped
    return false
  }

  const nextStepIndex = (() => {
    let i = currentStepIndex + 1
    while (i < STEPS.length && isSkippable(i)) i++
    return i < STEPS.length ? i : -1
  })()

  const prevStepIndex = (() => {
    let i = currentStepIndex - 1
    while (i >= 0 && isSkippable(i)) i--
    return i >= 0 ? i : -1
  })()

  // Redirect direct visits to a skipped step to the nearest reachable step.
  const redirectPathForSkipped = index => {
    for (let i = index + 1; i < STEPS.length; i++) {
      if (!isSkippable(i)) return STEPS[i].path
    }

    for (let i = index - 1; i >= 0; i--) {
      if (!isSkippable(i)) return STEPS[i].path
    }

    return FIRST_STEP_PATH
  }

  const ssiStepIndex = STEPS.findIndex(step => step.path === SSI_STEP_PATH)
  const goalsStepIndex = STEPS.findIndex(step => step.path === GOALS_STEP_PATH)
  const companyStockStepIndex = STEPS.findIndex(step => step.path === COMPANY_STOCK_STEP_PATH)
  const investmentsStepIndex = STEPS.findIndex(step => step.path === INVESTMENTS_STEP_PATH)

  // Build progress from reachable steps so its count, labels, and navigation
  // stay consistent with the actual flow.
  const visibleStepIndices = STEPS.map((_, index) => index).filter(index => !isSkippable(index))
  const visibleStepLabels = visibleStepIndices.map(index => STEPS[index].label)
  const currentVisiblePosition = visibleStepIndices.indexOf(currentStepIndex)

  const goNext = () => {
    if (nextStepIndex !== -1) goToStep(nextStepIndex)
  }

  const goBack = () => (prevStepIndex !== -1 ? goToStep(prevStepIndex) : navigate(exitTo))

  const isOnLastBuiltStep = nextStepIndex === -1
  const isSummary = currentPath === SUMMARY_STEP_PATH
  const isModified = !!store.modifiedCase

  const goToFirstStep = () => navigate(`/advice/${FIRST_STEP_PATH}`)

  // Hand off to `/implement` and let offboarding pick the first page: its route
  // table is what knows the per-template flow (retail, transaction,
  // institutional, PTD+) and whether the account is already managed. Deep-linking
  // a step from here skips whatever earlier steps that flow requires -- including
  // the `updateAcceptedCase` / `updateManagedAccountStatus` calls that actually
  // submit the accepted case.
  const acceptOrConfirm = () => {
    const acceptedCase = store.modifiedCase ?? store.recommendedCase
    if (acceptedCase == null) return

    store.setAcceptedCaseId(acceptedCase.caseId)
    // Match the `/overall` acceptance flow: offboarding reads
    // `acceptedContributionDistribution.catchUp` without a null check.
    store.setAcceptedContributionDistribution(
      buildInitialContributionDistribution({ acceptedCase, institutional: store.institutional })
    )
    navigate('/implement')
  }

  const undoChanges = async () => {
    store.resetModifiedCase()
    await store.refreshAllBreakdown().catch(err => console.error('Failed to load breakdown:', err))
  }

  const handleSsiDismiss = () => {
    store.setHideSsiAdviceStep(true)
    goNext()
  }

  // Hey Nic in SBSA is gated on the same feature flag + availability probe as the
  // standalone chat route.
  const chatAvailable = !!store.features?.enableHeyNic && !!store.aiStatus?.available
  const showChatPanel = chatAvailable && hasPanelRoom
  const showChatComment = chatAvailable && !hasPanelRoom
  const currentStepLabel = STEPS[currentStepIndex]?.label

  /*
   * Navigation is locked while a response streams in. Changing step re-points the
   * store's single active chat, which aborts the in-flight run — so the reply
   * would be lost rather than finishing in the background.
   *
   * Scoped to a stream one of the SBSA panels started, NOT any Hey Nic stream:
   * leaving /hey-nic mid-stream deliberately keeps that reply running, so it can
   * still be in flight when the user walks in here. Locking on it would strand
   * them on a step waiting for a conversation they've left.
   */
  const navLocked = store.adviceStreaming

  const progressIndicator =
    status === 'loading' ? null : (
      <AdviceProgressIndicator
        totalSteps={visibleStepIndices.length}
        currentStep={Math.max(currentVisiblePosition, 0) + 1}
        stepLabels={visibleStepLabels}
        onStepChange={
          navLocked
            ? undefined
            : position => {
                const stepIndex = visibleStepIndices[position - 1]
                if (stepIndex != null) goToStep(stepIndex)
              }
        }
      />
    )

  return (
    <AdviceLayout>
      {isMobile && progressIndicator}

      <AdviceTopNav
        engineType={engineType}
        onExit={() => navigate(exitTo)}
        onUndo={isSummary && isModified ? undoChanges : undefined}
        navLink={
          isSummary && !isMobileOrTablet ? (
            <WalkMeThroughLink type='button' onClick={goToFirstStep} disabled={navLocked}>
              Walk me through step-by-step
            </WalkMeThroughLink>
          ) : null
        }
      />

      {status === 'loading' ? (
        <PageLoading />
      ) : (
        <AdviceBody>
          <AdviceMain>
            {!isMobile && progressIndicator}

            <StepScrollArea>
              <StepScroll ref={scrollRef}>
                {/* Mobile entry point: the step's AI comment, which opens the
                    chat overlay. The design's floating action button is held back
                    for now — it would collide with the scroll-to-bottom button. */}
                {showChatComment && (
                  <StepContent>
                    <AdviceHeyNicComment onOpen={() => setChatOverlayOpen(true)} />
                  </StepContent>
                )}

                <Routes>
                  <Route
                    path={FIRST_STEP_PATH}
                    element={
                      <MonthlyRetirementIncome
                        onSkipToSummary={
                          navLocked ? undefined : () => navigate(`/advice/${SUMMARY_STEP_PATH}`)
                        }
                      />
                    }
                  />

                  <Route path='retirement-age' element={<RetirementAge />} />

                  <Route
                    path={SSI_STEP_PATH}
                    element={
                      ssiSkipped ? (
                        <Navigate to={`/advice/${redirectPathForSkipped(ssiStepIndex)}`} replace />
                      ) : (
                        <SSI onDismiss={handleSsiDismiss} dismissDisabled={navLocked} />
                      )
                    }
                  />

                  <Route path='contributions' element={<Contributions />} />

                  <Route
                    path={GOALS_STEP_PATH}
                    element={
                      goalsSkipped ? (
                        <Navigate
                          to={`/advice/${redirectPathForSkipped(goalsStepIndex)}`}
                          replace
                        />
                      ) : (
                        <Goals />
                      )
                    }
                  />

                  <Route
                    path={COMPANY_STOCK_STEP_PATH}
                    element={
                      companyStockSkipped ? (
                        <Navigate
                          to={`/advice/${redirectPathForSkipped(companyStockStepIndex)}`}
                          replace
                        />
                      ) : (
                        <CompanyStock />
                      )
                    }
                  />

                  <Route
                    path={INVESTMENTS_STEP_PATH}
                    element={
                      investmentsSkipped ? (
                        <Navigate
                          to={`/advice/${redirectPathForSkipped(investmentsStepIndex)}`}
                          replace
                        />
                      ) : (
                        <Investments />
                      )
                    }
                  />

                  <Route
                    path={SUMMARY_STEP_PATH}
                    element={
                      /* Summary's mobile button navigates, so it is locked the
                         same way as the top nav's desktop link. Withholding the
                         handler is what disables it (as with onSkipToSummary);
                         SSI's dismiss takes an explicit flag instead, because
                         there a missing handler already means something else. */
                      <Summary onWalkMeThrough={navLocked ? undefined : goToFirstStep} />
                    }
                  />

                  <Route path='*' element={<Navigate to={FIRST_STEP_PATH} replace />} />
                </Routes>
              </StepScroll>

              <ScrollToBottomButton
                absolute={!isMobile}
                getScrollElement={getScrollElement}
                watch={location.pathname}
              />
            </StepScrollArea>

            {isSummary ? (
              <AdviceFooter
                onBack={goBack}
                onNext={acceptOrConfirm}
                onUndo={undoChanges}
                nextLabel={isModified ? 'Confirm changes' : 'Accept recommendation'}
                nextLabelMobile={isModified ? 'Confirm' : 'Accept'}
                showUndo={isModified}
                backDisabled={navLocked}
                nextDisabled={navLocked}
              />
            ) : (
              <AdviceFooter
                onBack={goBack}
                onNext={goNext}
                backDisabled={navLocked}
                nextDisabled={isOnLastBuiltStep || navLocked}
                leftOfNext={
                  currentPath === SSI_STEP_PATH && ssiExcludedAll ? (
                    <DontShowAgain
                      checked={!!store.userSettings?.hideSsiAdviceStep}
                      onConfirm={handleSsiDismiss}
                      disabled={navLocked}
                    />
                  ) : null
                }
              />
            )}
          </AdviceMain>

          {showChatPanel && (
            <AdviceHeyNicPanel
              stepPath={currentPath}
              stepLabel={currentStepLabel}
              showInput={isSummary}
              onCaseRefresh={refreshAdviceCase}
            />
          )}

          {showChatComment && (
            <AdviceHeyNicOverlay
              open={chatOverlayOpen}
              stepPath={currentPath}
              stepLabel={currentStepLabel}
              showInput={isSummary}
              onClose={() => setChatOverlayOpen(false)}
              onCaseRefresh={refreshAdviceCase}
            />
          )}

          {caseRefreshing && <LoadingOverlay label='Updating your plan' />}
        </AdviceBody>
      )}
    </AdviceLayout>
  )
}

export default withRouterProps(inject('store')(observer(Advice)))
